/**
 * Theme model based on API response structure
 */

/**
 * @typedef {Object} Language
 * @property {string} code - Language code (e.g., "fr", "en", "es")
 */

/**
 * @typedef {Object} ThemeTranslation
 * @property {string} themeName - The theme name in the specific language
 * @property {Language} language - The language object
 */

/**
 * @typedef {Object} User
 * @property {string} id - User ID
 */

/**
 * @typedef {Object} Theme
 * @property {string} id - Theme ID
 * @property {string} createdAt - Creation date in ISO format
 * @property {string} updatedAt - Last update date in ISO format
 * @property {User} user - User object containing the user ID
 * @property {ThemeTranslation[]} themeTranslations - Array of theme translations
 * @property {string|null} [deletedAt] - Soft delete timestamp from API
 */

/**
 * Get the theme name in the specified language
 * @param {Theme} theme - The theme object
 * @param {string} languageCode - The language code to get the name for (default: "fr")
 * @returns {string} The theme name in the specified language
 */
export function getThemeName(theme, languageCode = "en") {
  if (!theme || !theme.themeTranslations || !Array.isArray(theme.themeTranslations)) {
    return "Untitled Theme";
  }
  
  // Try to find the exact language match
  const translation = theme.themeTranslations.find(t => t.language?.code === languageCode);
  if (translation?.themeName) {
    return translation.themeName;
  }
  
  
  return null
}

/**
 * Transform API theme data to internal format
 * @param {Theme} apiTheme - Theme data from API
 * @param {string} commonLanguage - Common language code for the user
 * @returns {Object} Transformed theme object for internal use
 */
export function transformTheme(apiTheme, commonLanguage = "fr") {
  if (!apiTheme || typeof apiTheme !== 'object') {
    throw new Error('Invalid theme data provided to transformTheme');
  }

  if (getThemeName(apiTheme, commonLanguage) === null) {
    return null;
  }

  return {
    id: apiTheme.id || '',
    nom: getThemeName(apiTheme, commonLanguage),
    userId: apiTheme.user?.id || null,
    createdAt: apiTheme.createdAt || new Date().toISOString(),
    updatedAt: apiTheme.updatedAt || new Date().toISOString(),
    deletedAt: apiTheme.deletedAt || null, // Use actual deletedAt from API response
    themeTranslations: Array.isArray(apiTheme.themeTranslations) ? apiTheme.themeTranslations : []
  };
}
